/**
 * Extracts initials from a display name, handling various name formats including
 * single names, multiple names, and names with suffixes.
 *
 * @param displayName - The full display name to extract initials from
 * @returns A string of 1-2 characters representing the name's initials
 *
 * @example
 * getInitials("John Doe") // returns "JD"
 * getInitials("Mary") // returns "MA"
 * getInitials("Martin Luther King Jr") // returns "MK"
 */
export const getInitials = (displayName: string): string => {
  const fallback = "UR";

  if (!displayName) return fallback;

  const trimmedName = displayName.trim();
  if (!trimmedName) return fallback;

  const names = trimmedName.split(/\s+/);

  if (names.length === 0) return fallback;

  const firstWord = names[0];
  if (!firstWord) return "";

  if (names.length === 1) {
    // For single names, take up to first two characters
    return firstWord.substring(0, 2).toUpperCase();
  }

  // Common name suffixes to exclude
  const suffixes = ["jr", "sr", "ii", "iii", "iv", "v"];

  // For multiple names, try to get first and last name initials
  const firstName = firstWord;
  let lastName = names[names.length - 1];

  // Safety check - if somehow lastName is undefined, use firstName
  if (!lastName) {
    lastName = firstName;
  }

  // If the last word is a suffix, use the word before it
  if (suffixes.includes(lastName.toLowerCase())) {
    const previousName = names[names.length - 2];
    lastName = previousName || firstName;
  }

  return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
};
